import React, { useRef, useState } from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";
import TextareaAutosize from "react-textarea-autosize";

import Colors from "../../../../Resources/Color";
import { useDevice } from "../../../_layout";
import {
  RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT,
  RECIPE_EDITOR_NAME_INPUT_HEIGHT,
} from "../../recipes/recipeLogic";
import {
  MAX_NUTRITION_LONG_TEXT_LENGTH,
  MAX_NUTRITION_NAME_LENGTH,
} from "../../nutritionLimits";
import { useDesktopAutoFocusTextInput } from "../../shared/useDesktopAutoFocusTextInput";

export function NutritionPlanEditorDetailsSection({
  t,
  draft,
  updateField,
}) {
  const { isMobile } = useDevice();
  const [isNameHovered, setIsNameHovered] = useState(false);
  const [isNotesHovered, setIsNotesHovered] = useState(false);
  const nameInputRef = useRef(null);
  const shouldAutoFocusNameRef = useRef(
    String(draft?.name ?? "").trim().length === 0,
  );
  const notesLineHeight = 20;
  const notesVerticalPadding = Math.max(
    0,
    (RECIPE_EDITOR_NAME_INPUT_HEIGHT - notesLineHeight) / 2,
  );
  const notesMaxHeight = notesLineHeight * 8 + notesVerticalPadding * 2;
  useDesktopAutoFocusTextInput(nameInputRef, {
    enabled: shouldAutoFocusNameRef.current,
    isMobile,
  });

  return (
    <View style={styles.container}>
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>{t("name-string")}</Text>
        <View
          onMouseEnter={!isMobile ? () => setIsNameHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsNameHovered(false) : undefined}
          style={[
            styles.nameInputShell,
            isNameHovered ? styles.nameInputShellHovered : null,
          ]}
        >
          <TextInput
            ref={nameInputRef}
            value={draft?.name ?? ""}
            onChangeText={(value) => updateField("name", value)}
            maxLength={MAX_NUTRITION_NAME_LENGTH}
            placeholder={t(
              "nutritionPlanNamePlaceholder-string",
              "Training day - high carbs",
            )}
            placeholderTextColor={Colors.grayColor}
            style={styles.nameInput}
            accessibilityLabel={t("name-string")}
            aria-label={t("name-string")}
          />
        </View>
      </View>

      <View style={styles.field}>
        <Text style={styles.fieldLabel}>{t("notes-string", "Notes")}</Text>
        <View
          onMouseEnter={!isMobile ? () => setIsNotesHovered(true) : undefined}
          onMouseLeave={!isMobile ? () => setIsNotesHovered(false) : undefined}
          style={[
            styles.nameInputShell,
            styles.notesInputShell,
            { maxHeight: notesMaxHeight },
            isNotesHovered ? styles.nameInputShellHovered : null,
          ]}
        >
          <TextareaAutosize
            className="nutrition-editor-detail-textarea"
            value={draft?.notes ?? ""}
            onChange={(event) => {
              const nextValue =
                event?.currentTarget?.value ?? event?.target?.value ?? "";
              updateField("notes", nextValue);
            }}
            placeholder={t(
              "nutritionPlanNotesPlaceholder-string",
              "Add optional notes or instructions for this plan",
            )}
            minRows={1}
            maxRows={8}
            maxLength={MAX_NUTRITION_LONG_TEXT_LENGTH}
            spellCheck={false}
            style={{
              flex: 1,
              width: "100%",
              display: "block",
              resize: "none",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              border: "none",
              outline: "none",
              fontSize: 13,
              fontWeight: "400",
              color: Colors.blackColor,
              backgroundColor: "transparent",
              paddingLeft: 12,
              paddingRight: 12,
              margin: 0,
              boxSizing: "border-box",
              paddingTop: notesVerticalPadding,
              paddingBottom: notesVerticalPadding,
              lineHeight: `${notesLineHeight}px`,
              maxHeight: notesMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  field: {
    gap: 10,
  },
  fieldLabel: {
    fontSize: 12,
    lineHeight: RECIPE_EDITOR_FIELD_LABEL_LINE_HEIGHT,
    fontWeight: "600",
    color: Colors.darkgrayColor,
    letterSpacing: 0.2,
  },
  nameInputShell: {
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    minHeight: RECIPE_EDITOR_NAME_INPUT_HEIGHT,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    transition:
      "border-color 180ms ease-in-out, background-color 180ms ease-in-out",
  },
  nameInputShellHovered: {
    borderColor: Colors.grayColor,
  },
  nameInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 10,
  },
  notesInputShell: {
    alignItems: "stretch",
    overflow: "hidden",
    paddingHorizontal: 0,
  },
});
